import {
  TextField,
  SelectField,
  CheckboxField,
  EntityAutocompleteField,
  type CreateDataDomainFormData,
} from '../forms';
import { createDataDomainSchema } from '../forms/validation';
import { EntityForm } from '../shared';
import { useAllStakeholders, useBusinessUnits } from '../../graphql/queries';
import {
  classificationOptions,
  criticalityLevelOptions,
  lifecycleStatusOptions,
} from '../../lib/selectOptions';
import '@tylertech/forge/card';
import './DataDomainForm.css';

export interface DataDomainFormProps {
  defaultValues?: Partial<CreateDataDomainFormData>;
  onSubmit: (data: CreateDataDomainFormData) => void | Promise<void>;
  onCancel: () => void;
  isSubmitting?: boolean;
  mode: 'create' | 'edit';
}

const domainTypeOptions = [
  { value: 'master', label: 'Master' },
  { value: 'reference', label: 'Reference' },
  { value: 'transactional', label: 'Transactional' },
  { value: 'analytical', label: 'Analytical' },
  { value: 'metadata', label: 'Metadata' },
];

const CardElement = 'forge-card' as React.ElementType;

export function DataDomainForm({
  defaultValues,
  onSubmit,
  onCancel,
  isSubmitting = false,
  mode,
}: DataDomainFormProps): JSX.Element {
  // Data Steward / Technical Owner are User IDs (meta-model-spec.md) but the
  // admin-gated REST `/users` endpoint isn't readable by whichever role
  // creates/edits DataDomains, so both source from the broadly-readable
  // Stakeholder catalog instead — same pattern as BusinessObjectiveForm's
  // Owner/Sponsor pickers (issue #771).
  const { data: stakeholdersData } = useAllStakeholders();
  const stakeholderOptions = (stakeholdersData?.stakeholders?.edges ?? []).map(({ node }) => ({
    value: node.entityId,
    label: node.name,
  }));

  const { data: businessUnitsData } = useBusinessUnits();
  const businessUnitOptions = (businessUnitsData?.businessUnits?.edges ?? []).map(({ node }) => ({
    value: node.entityId,
    label: node.name,
  }));

  return (
    <EntityForm<CreateDataDomainFormData>
      schema={createDataDomainSchema}
      baseValues={{
        lifecycleStatus: 'active',
        criticalityLevel: 'medium',
        defaultClassification: 'internal',
        containsPII: false,
        containsPCI: false,
        containsPHI: false,
      }}
      defaultValues={defaultValues}
      onSubmit={onSubmit}
      onCancel={onCancel}
      isSubmitting={isSubmitting}
      mode={mode}
      createLabel="Create Data Domain"
      editLabel="Update Data Domain"
      formClassName="data-domain-form"
      actionsClassName="form-actions form-actions--compact"
    >
      {({ register, formState: { errors }, values: formValues, setValue }) => (
        <>
          <CardElement className="form-card form-card--compact">
            <div className="form-section form-section--compact">
              <h2 className="forge-typography--headline6">Basic Information</h2>

              <TextField
                label="Data Domain Name"
                required
                error={errors.name}
                {...register('name')}
              />

              <TextField
                label="Description"
                error={errors.description}
                {...register('description')}
              />

              <SelectField
                label="Domain Type"
                required
                options={domainTypeOptions}
                error={errors.domainType}
                /* c8 ignore next */
                value={formValues.domainType ?? ''}
                {...register('domainType')}
              />

              <SelectField
                label="Criticality Level"
                required
                options={criticalityLevelOptions}
                error={errors.criticalityLevel}
                /* c8 ignore next */
                value={formValues.criticalityLevel ?? ''}
                {...register('criticalityLevel')}
              />

              <SelectField
                label="Lifecycle Status"
                options={lifecycleStatusOptions}
                error={errors.lifecycleStatus}
                /* c8 ignore next */
                value={formValues.lifecycleStatus ?? ''}
                {...register('lifecycleStatus')}
              />
            </div>
          </CardElement>

          <CardElement className="form-card form-card--compact">
            <div className="form-section form-section--compact">
              <h2 className="forge-typography--headline6">Ownership &amp; Governance</h2>

              <EntityAutocompleteField
                name="dataStewardId"
                label="Data Steward"
                required
                options={stakeholderOptions}
                value={formValues.dataStewardId ?? ''}
                onChange={(entityId) =>
                  setValue('dataStewardId', entityId, { shouldValidate: true })
                }
                error={errors.dataStewardId}
                placeholder="Search stakeholders…"
                helperText="The stakeholder accountable for this data domain"
              />

              <EntityAutocompleteField
                name="owningBusinessUnitId"
                label="Owning Business Unit"
                required
                options={businessUnitOptions}
                value={formValues.owningBusinessUnitId ?? ''}
                onChange={(entityId) =>
                  setValue('owningBusinessUnitId', entityId, { shouldValidate: true })
                }
                error={errors.owningBusinessUnitId}
                placeholder="Search business units…"
              />

              <EntityAutocompleteField
                name="technicalOwnerId"
                label="Technical Owner"
                options={stakeholderOptions}
                value={formValues.technicalOwnerId ?? ''}
                onChange={(entityId) =>
                  setValue('technicalOwnerId', entityId, { shouldValidate: true })
                }
                error={errors.technicalOwnerId}
                placeholder="Search stakeholders…"
                helperText="Optional technical owner"
              />
            </div>
          </CardElement>

          <CardElement className="form-card form-card--compact">
            <div className="form-section form-section--compact">
              <h2 className="forge-typography--headline6">Classification &amp; Compliance</h2>

              <SelectField
                label="Default Classification"
                required
                options={classificationOptions}
                error={errors.defaultClassification}
                /* c8 ignore next */
                value={formValues.defaultClassification ?? ''}
                {...register('defaultClassification')}
              />

              <div className="checkbox-group">
                <CheckboxField
                  label="Contains PII (Personally Identifiable Information)"
                  error={errors.containsPII}
                  {...register('containsPII')}
                />

                <CheckboxField
                  label="Contains PCI (Payment Card Industry Data)"
                  error={errors.containsPCI}
                  {...register('containsPCI')}
                />

                <CheckboxField
                  label="Contains PHI (Protected Health Information)"
                  error={errors.containsPHI}
                  {...register('containsPHI')}
                />
              </div>

              <TextField label="Notes" error={errors.notes} {...register('notes')} />

              <TextField
                label="Documentation URL"
                error={errors.documentationUrl}
                {...register('documentationUrl')}
              />
            </div>
          </CardElement>
        </>
      )}
    </EntityForm>
  );
}
